import React, { useEffect, useState, useRef } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { Box } from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { updateDateRangeEmissions, updateDateRangeConsumption, updateDateRangeFinancials } from "api";
import { COLORS, Loading } from "components";
import { sortedDates } from "utils";
import { chartColors, commonStyles } from "../../styles";
import { DateRangeChartProps } from "../../types";
import { ChartDataPoint, ChartDetailsPanel } from "../chartDetailsPanel";
import { createTooltipConfig } from "../chartTooltip";
import { generateXAxisCategories, getEmissionType } from "../../utils";

interface SelectedBarData {
    category: string;
    total: number;
    points: ChartDataPoint[];
    unit: string;
}

interface DateRangeChartComponentProps extends DateRangeChartProps {
    chartType: string;
    dataKey: string;
    generateChartData: (data: any[], emissionType: string) => any;
    generateSeriesData: (chartData: any, sortedCategories: string[]) => any;
    getSeriesNames?: (key: string) => string;
    sharedTotalData?: any[];
    isLoadingTotal?: boolean;
}

export const DateRangeChartComponent = ({
    chartTitle,
    dateRangeType,
    subTabName,
    startDate,
    endDate,
    searchFilters,
    currentMainTab,
    chartType,
    dataKey,
    generateChartData,
    generateSeriesData,
    getSeriesNames = (key) => key,
    sharedTotalData = [],
    isLoadingTotal = false,
}: DateRangeChartComponentProps) => {
    const [dateRangeData, setDateRangeData] = useState<any>([]);
    const [selectedBarData, setSelectedBarData] = useState<SelectedBarData | null>(null);
    const chartRef = useRef<HighchartsReact.RefObject>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const apiFn = currentMainTab === "GHG Emissions" ? updateDateRangeEmissions : currentMainTab === "Consumption" ? updateDateRangeConsumption : updateDateRangeFinancials;
    const { mutateAsync: updateDateRange, isLoading } = useMutation(apiFn);

    const noDataAvailable = !isLoading && Object.values(dateRangeData).every(value =>
        typeof value === "object" && value !== null && Object.keys(value).length === 0
    );

    const chartData = dateRangeData[dataKey];
    const isEmissionsChart = subTabName === "location" || subTabName === "market";
    const isConsumption = currentMainTab === "Consumption";

    const data: any[] = chartData ? Object.values(chartData).flat() : [];
    const emissionType = getEmissionType(isEmissionsChart, isConsumption, subTabName);

    const categoriesXAxis = generateXAxisCategories(data, dateRangeType);
    const sortedCategoriesXAxis = sortedDates(categoriesXAxis);
    const chartDataResult = generateChartData(data, emissionType);
    const seriesData = generateSeriesData(chartDataResult, sortedCategoriesXAxis);

    const unitYAxis = isEmissionsChart ? "tCO2e" : data[0]?.unit ?? data[0]?.currency ?? "No Data Available";

    useEffect(() => {
        const fetchDateRange = async () => {
            setDateRangeData({});
            await updateDateRange({
                dateRangeRequest: {
                    ...searchFilters,
                    compareFromDate: startDate.toISOString().substring(0, 10),
                    compareToDate: endDate.toISOString().substring(0, 10),
                    groupBy: dateRangeType
                },
                chartType
            }, {
                onSuccess: (response: any) => {
                    setDateRangeData(response.data);
                },
            })
        };

        fetchDateRange();
    }, [searchFilters, startDate, endDate, dateRangeType, updateDateRange, chartType]);

    const handleBarClick = (point: any) => {
        const category = String(point.category);
        const points: ChartDataPoint[] = [];

        // Collect data for all series at this category
        Object.keys(seriesData).forEach((seriesKey, index) => {
            const seriesDataArray = seriesData[seriesKey as keyof typeof seriesData];
            if (Array.isArray(seriesDataArray)) {
                const value = seriesDataArray[Number(point.index)];
                if (value !== undefined && value !== null) {
                    points.push({
                        seriesName: getSeriesNames(seriesKey),
                        value: Number(value),
                        color: chartColors[index % chartColors.length],
                    });
                }
            }
        });

        // Get total value from shared data
        let total = 0;
        if (Array.isArray(sharedTotalData)) {
            const totalValue = sharedTotalData[Number(point.index)];
            if (totalValue !== undefined && totalValue !== null) {
                total = totalValue;
                points.push({
                    seriesName: "Total",
                    value: totalValue,
                    color: "transparent"
                });
            }
        }

        setSelectedBarData({
            category,
            points,
            total,
            unit: unitYAxis,
        });
    };

    const handleClosePanel = () => {
        setSelectedBarData(null);
    };

    const chartOptions = {
        chart: {
            type: "column",
            spacing: [30, 50, 20, 40],
            width: null,
            height: 450,
            reflow: true,
            style: {
                width: '100%',
                height: '100%'
            },
            animation: true,
        },
        title: {
            text: `${chartTitle}${noDataAvailable ? ": No Data Available" : ''}`,
            align: "left",
            margin: 40,
            style: {
                ...commonStyles,
                color: COLORS.Mainblue,
                fontSize: "20px",
            },
        },
        xAxis: {
            categories: sortedCategoriesXAxis,
            labels: {
                style: commonStyles,
            },
            startOnTick: false,
            endOnTick: false,
        },
        yAxis: {
            title: {
                text: unitYAxis,
                style: commonStyles,
                offset: 50
            },
            labels: {
                style: commonStyles,
            },
        },
        series: [
            {
                name: "Total",
                type: "line",
                data: sharedTotalData,
                color: "transparent",
                dataLabels: {
                    enabled: true,
                    verticalAlign: "bottom",
                    style: {
                        ...commonStyles,
                        textOutline: "none",
                    },
                },
            },
            ...Object.keys(seriesData).map((seriesKey, index) => ({
                name: getSeriesNames(seriesKey),
                data: seriesData[seriesKey as keyof typeof seriesData],
                color: chartColors[index % chartColors.length],
                fillOpacity: 0.3,
                lineWidth: 6,
                marker: { enabled: false },
            })),
        ],
        plotOptions: {
            line: {
                stacking: "normal",
                dataLabels: {
                    enabled: true,
                    style: {
                        fontWeight: "bold",
                        textOutline: "",
                    },
                    formatter(this: Highcharts.TooltipFormatterContextObject) {
                        return `<b>${Number(this!.y?.toFixed(2)).toLocaleString('en-US')}</b>`;
                    },
                },
            },
            column: {
                stacking: "normal",
                cursor: "pointer",
                animation: true,
                dataLabels: {
                    enabled: true,
                    style: {
                        fontWeight: "bold",
                        textOutline: "",
                    },
                    formatter(this: Highcharts.TooltipFormatterContextObject) {
                        return `<b>${Number(this!.y?.toFixed(2)).toLocaleString('en-US')}</b>`;
                    },
                },
                point: {
                    events: {
                        click() {
                            handleBarClick(this);
                        }
                    }
                }
            },
        },
        legend: {
            title: {
                style: commonStyles,
            },
            align: "left",
            verticalAlign: "bottom",
            x: 20,
            margin: 30,
            maxHeight: 100,
            itemDistance: 10
        },
        tooltip: createTooltipConfig(unitYAxis),
        credits: {
            enabled: false,
        },
    };

    return (
        <Box
            ref={containerRef}
            key={selectedBarData ? 'panel-open' : 'panel-closed'}
            sx={{
                display: "flex",
                gap: '2px',
                fontFamily: "Inter",
                "& > div": {
                    borderRadius: 2,
                },
            }}
        >
            <Box sx={{
                display: "flex",
                flexDirection: "column",
                width: selectedBarData ? "75%" : "100%",
                transition: "width 0.3s ease-in-out",
                "& > div": {
                    borderRadius: 2,
                },
            }}>
                {(isLoading || isLoadingTotal) && <Box sx={{ height: 300, position: "absolute", zIndex: 99999, right: "50%" }}>
                    <Loading />
                </Box>}
                <HighchartsReact
                    ref={chartRef}
                    highcharts={Highcharts}
                    options={chartOptions}
                />
            </Box>

            <ChartDetailsPanel
                isOpen={!!selectedBarData}
                onClose={handleClosePanel}
                title={chartTitle}
                category={selectedBarData?.category || ""}
                total={selectedBarData?.total}
                unit={selectedBarData?.unit || ""}
                points={selectedBarData?.points || []}
                height={450}
            />
        </Box>
    );
}; 